import { Transport } from "./transport.js";
import { ChatService } from "./chat.js";
import { ModelsService } from "./models.js";
import { ImageService } from "./image.js";
import { AudioService } from "./audio.js";
import { VideoService } from "./video.js";
import { ComputerService } from "./computer.js";
import { FilesService } from "./files.js";
import { EmbeddingsService } from "./embeddings.js";
import { BrowserAuth, type BrowserUser } from "./browser-auth.js";
import { SDKEvents } from "./events.js";

// The platform, not the bare gateway: personal rk_ keys are checked and
// billed by www.runapps.ai, which forwards to the gateway. api.runapps.ai
// only knows the gateway's own keys.
const DEFAULT_BASE_URL = "https://www.runapps.ai";

/**
 * Auth strategy.
 *
 *   - `"static"` (default): use `apiKey` or `apiKeyResolver` directly.
 *   - `"runapps"`: opt into the built-in browser auth flow that
 *     handshakes with `https://www.runapps.ai/api/sdk/grant`.  No
 *     external auth.js script needed; the SDK consumes the URL
 *     fragment after the redirect-back, exposes `client.user` and
 *     `client.signIn()`, and renders an identity badge.  Only useful
 *     in browsers — in Node this falls back to throwing the same
 *     "missing apiKey" error you'd get with no auth config.
 */
// "runapps" is the current name of the platform sign-in; "runjobs" is the
// same thing under the name from before the rename.
export type AuthProvider = "static" | "runapps" | "runjobs";

export interface ClientOptions {
  /** Static gateway API key (typically prefixed `gw-` / `rj_` / `rrt_`).
   *  Required unless `apiKeyResolver` or `authProvider: "runapps"` is supplied. */
  apiKey?: string;
  /**
   * Dynamic API key resolver.  See `AuthProvider` for the typical
   * runapps.ai browser-bundle wiring.
   *
   * Wins over `apiKey` when both are supplied.
   */
  apiKeyResolver?: () => string | Promise<string>;
  /**
   * Auth strategy — defaults to `"static"`.  Pass `"runapps"` to
   * activate the built-in browser auth flow against runapps.ai;
   * baseURL defaults to `https://www.runapps.ai` in that mode.
   */
  authProvider?: AuthProvider;
  /**
   * Show the floating identity badge in `runjobs` auth mode.
   * **Default `true`** — the badge is now a desktop-ball-style
   * real-time activity indicator (LED dot + progress ring around
   * the avatar + click-through popover with active calls / recent
   * completions / session totals). Set `false` only when the
   * bundle ships its own status UI and you don't want the platform
   * badge in the corner.
   */
  showIdentityBadge?: boolean;
  /**
   * Which corner the identity badge floats in. Defaults to
   * `"bottom-right"` — the canonical tray-app position. Switch to
   * `"bottom-left"` when the bundle's own UI occupies the right
   * gutter (e.g. a chat dock); the top corners are useful for
   * fullscreen / portrait kiosk layouts.
   *
   * The popover anchors itself to the same corner — bottom-left
   * badge → popover opens up + right of the badge; top-* badges →
   * popover opens down. Each corner only nudges the CSS; the badge
   * + popover content is identical.
   */
  badgePosition?: "bottom-right" | "bottom-left" | "top-right" | "top-left";
  /**
   * @deprecated Use `showIdentityBadge` instead.  Kept for back-compat;
   * acts as `showIdentityBadge: !hideIdentityBadge` when set.
   */
  hideIdentityBadge?: boolean;
  /**
   * Pin the runapps.ai grant flow to a specific project (only used
   * when `authProvider: "runapps"`).  When set, `/api/sdk/grant`
   * receives `project_id=<value>` and mints an `rrt_*` resource
   * token bound to THAT project — required for `client.files.*`
   * calls from any origin that isn't a registered (origin, app)
   * pair (e.g. a bundle running on `localhost:5173` during dev).
   *
   * Production bundles served from a registered origin can omit
   * this; the gateway derives the project from the (origin, app)
   * pair.
   */
  project?: string;
  /** Override the default base URL, `https://www.runapps.ai` (the
   *  platform: personal keys and app tokens are both accepted there). */
  baseURL?: string;
  /** Optional fetch override (e.g. node-fetch with custom agent). */
  fetch?: typeof fetch;
}

/**
 * Top-level RunApps SDK client.  Construct once, share across services.
 *
 * Browser bundle (resource projects on runapps.ai):
 *
 * ```html
 * <script src="https://cdn.jsdelivr.net/npm/@runjobsai/sdk/dist/sdk.umd.js"></script>
 * <script>
 *   const client = new RunApps.Client({ authProvider: "runapps" });
 *   const res = await client.chat.create({
 *     model: "gpt-4o-mini",
 *     messages: [{ role: "user", content: "hello" }],
 *   });
 * </script>
 * ```
 *
 * Node / server-side:
 *
 * ```ts
 * import { RunApps } from "@runjobsai/sdk";
 * const client = new RunApps({ apiKey: process.env.RUNJOBS_API_KEY! });
 * ```
 */
export class RunApps {
  readonly chat: ChatService;
  readonly models: ModelsService;
  readonly image: ImageService;
  readonly audio: AudioService;
  readonly video: VideoService;
  readonly computer: ComputerService;
  readonly files: FilesService;
  readonly embeddings: EmbeddingsService;

  /**
   * Browser auth helper, populated only when `authProvider: "runapps"`.
   * Use `client.signIn()` and `client.user` for the common cases; the
   * full instance is exposed for advanced flows (manual `onTokenChange`
   * subscription, etc.).
   */
  readonly auth: BrowserAuth | null = null;

  /**
   * Runtime event bus for SDK-call telemetry. Every LLM-ish service
   * (chat / embeddings / image / audio / video / computer) fires
   * `request:start`, `request:streamDelta`, `request:end`, and
   * `request:error` events here so UI overlays — like the bottom-right
   * identity badge's activity ring — can render real-time state
   * without business code threading anything through.
   *
   * Zero-cost when nobody subscribes (emit is a few property reads).
   * Subscribe via `client.events.on("request:start", handler)` — the
   * returned closure is the unsubscribe.
   */
  readonly events: SDKEvents = new SDKEvents();

  constructor(options: ClientOptions = {}) {
    const provider = options.authProvider ?? "static";
    let apiKeyResolver = options.apiKeyResolver;
    let baseURL = options.baseURL;
    let onUnauthorized: (() => void) | undefined;

    if (provider === "runapps" || provider === "runjobs") {
      // The grant page (/api/sdk/grant) and the gateway both live on
      // www.runapps.ai.  An explicit baseURL keeps control.
      baseURL = baseURL ?? "https://www.runapps.ai";
      // Badge default: SHOWN. The badge is now a real-time activity
      // indicator (LED + ring + popover) — useful enough that we'd
      // rather have the rare "I already have my own UI" bundle opt
      // OUT with `showIdentityBadge: false` than have every bundle
      // miss the live feedback by default. The legacy
      // `hideIdentityBadge` flag still wins when set, for callers
      // that explicitly suppressed the badge under the old default.
      const showBadge =
        options.showIdentityBadge !== undefined
          ? options.showIdentityBadge
          : options.hideIdentityBadge !== undefined
            ? !options.hideIdentityBadge
            : true;
      const auth = new BrowserAuth({
        origin: baseURL,
        hideBadge: !showBadge,
        // Pipe the shared event bus into BrowserAuth so the badge can
        // render its real-time activity ring / LED / popover. Safe to
        // pass even when the caller never subscribes — emit is a few
        // property reads when nobody's listening.
        events: this.events,
        ...(options.badgePosition !== undefined && { badgePosition: options.badgePosition }),
        ...(options.project !== undefined && { project: options.project }),
      });
      (this as { auth: BrowserAuth | null }).auth = auth;
      // The browser auth resolver wins over any caller-supplied one
      // because the auth flow owns token issuance + refresh from now on.
      apiKeyResolver = auth.getToken;
      // 401 from the gateway → token was revoked server-side (typically
      // because the user unsubscribed in another tab).  Drop the cached
      // token so the transport's auto-retry hits a fresh signIn() flow.
      onUnauthorized = () => auth.invalidate();
    }

    if (!options.apiKey && !apiKeyResolver) {
      throw new Error(
        "runapps: pass either `apiKey`, `apiKeyResolver`, or `authProvider: \"runapps\"`",
      );
    }
    const transport = new Transport({
      baseURL: baseURL ?? DEFAULT_BASE_URL,
      ...(options.apiKey !== undefined && { apiKey: options.apiKey }),
      ...(apiKeyResolver !== undefined && { apiKeyResolver }),
      ...(onUnauthorized !== undefined && { onUnauthorized }),
      ...(options.fetch ? { fetchImpl: options.fetch } : {}),
    });
    // Service constructors receive the shared event bus so each call
    // path (chat/embeddings/image/audio/video/computer) can emit
    // start/end/error around its network operations. Files and Models
    // intentionally don't take it — they're infrastructure, not
    // user-facing LLM calls, and surfacing them would clutter the
    // badge with /v1/models polls.
    this.chat = new ChatService(transport, this.events);
    this.models = new ModelsService(transport);
    this.image = new ImageService(transport, this.events);
    this.audio = new AudioService(transport, this.events);
    this.video = new VideoService(transport, this.events);
    this.computer = new ComputerService(transport, this.events);
    // `project` also scopes the file URLs (/v1/p/<project>/files/...),
    // so two apps never issue byte-identical file requests.  Omitted →
    // legacy unscoped /v1/files/... paths.
    this.files = new FilesService(transport, options.project);
    this.embeddings = new EmbeddingsService(transport, this.events);
  }

  /** Force a redirect to the runapps.ai grant page.  No-op in static
   *  auth mode or in Node. */
  signIn(): void {
    this.auth?.signIn();
  }

  /**
   * Clear the cached token + identity from memory and localStorage.
   * The next API call will trigger sign-in again (redirect to grant
   * page).  Wire this to a "Sign out" button in your settings UI.
   *
   * No-op in static auth mode or in Node.
   */
  signOut(): void {
    this.auth?.signOut();
  }

  /** Currently-authenticated user (runapps auth mode), or null. */
  get user(): BrowserUser | null {
    return this.auth?.user ?? null;
  }
}
